import { buildSnapshot } from './replay';
import type { RaceEvent, RaceRecording, RaceSnapshot } from './race';
import { raceVisuals, revealRaceVisuals, type RaceVisuals } from './race-visuals';

export interface SessionChartProjectionInput {
  recording?: RaceRecording;
  snapshot: RaceSnapshot;
  presentation: 'results' | 'live' | 'replay';
  visualHead: number;
}

export interface SessionChartProjection {
  recording?: RaceRecording;
  visualHead: number;
  visualCoverage: RaceRecording['coverage'] | RaceSnapshot['coverage'];
  sourceEndMs: number;
  modelEndMs: number;
  model: RaceVisuals;
}

export interface SessionChartBase {
  recording?: RaceRecording;
  sourceEndMs: number;
  visualCoverage: RaceRecording['coverage'] | RaceSnapshot['coverage'];
  /**
   * Only facts that can change the chart are indexed.  The live stream also
   * carries freshness, weather and telemetry events; those must not force a
   * chart projection or make a later non-visual update look like a chart
   * observation.
   */
  chartEvents: readonly RaceEvent[];
  /** The projection cache is keyed by the inclusive event frontier. */
  projectionCache: Map<number, RaceVisuals>;
  projected: RaceVisuals;
}

const chartTimingPatch = (event: Extract<RaceEvent, { type: 'timing' }>) =>
  Object.hasOwn(event.patch, 'position') || Object.hasOwn(event.patch, 'lap')
  || Object.hasOwn(event.patch, 'status') || Object.hasOwn(event.patch, 'recentLaps');

function isChartEvent(event: RaceEvent) {
  return event.type === 'pit' || event.type === 'status' || event.type === 'timing' && chartTimingPatch(event);
}

// A projection contains the complete rank and pace history up to its
// frontier. Keep a small recent window so a long live session cannot turn
// frontier caching into quadratic memory growth.
const MAX_PROJECTION_CACHE = 8;

function upperBound(events: readonly RaceEvent[], atMs: number) {
  let low = 0, high = events.length;
  while (low < high) {
    const middle = low + Math.floor((high - low) / 2);
    if (events[middle].atMs <= atMs) low = middle + 1;
    else high = middle;
  }
  return low;
}

function prefixRecording(recording: RaceRecording, events: readonly RaceEvent[], sourceEndMs: number): RaceRecording {
  return { ...recording, events: [...events], durationMs: sourceEndMs };
}

function boundedSnapshot(recording: RaceRecording, events: readonly RaceEvent[], sourceEndMs: number, atMs: number) {
  const prefix = prefixRecording(recording, events, sourceEndMs);
  return buildSnapshot(prefix, Math.max(0, Math.min(sourceEndMs, atMs)));
}

function emptyChart(recording: RaceRecording | undefined, snapshot: RaceSnapshot, sourceEndMs: number) {
  if (!recording) return raceVisuals(undefined, snapshot, { evidenceEndMs: sourceEndMs });
  const empty = prefixRecording(recording, [], sourceEndMs);
  return raceVisuals(empty, buildSnapshot(empty, 0), { evidenceEndMs: sourceEndMs });
}

/** Index chart facts once. Cursor changes project only the newly crossed prefix. */
export function projectSessionChartBase(recording: RaceRecording | undefined, snapshot: RaceSnapshot): SessionChartBase {
  const sourceEndMs = Math.max(1, recording?.durationMs ?? snapshot.atMs);
  const visualCoverage = recording?.coverage ?? snapshot.coverage;
  const chartEvents = [...new Map((recording?.events ?? []).filter(isChartEvent).map(event => [event.id, event])).values()]
    .sort((a, b) => a.atMs - b.atMs || a.id.localeCompare(b.id));
  const projectionCache = new Map<number, RaceVisuals>();
  const projected = emptyChart(recording, snapshot, sourceEndMs);
  projectionCache.set(0, projected);
  return { recording, sourceEndMs, visualCoverage, chartEvents, projectionCache, projected };
}

function projectionAt(base: SessionChartBase, snapshot: RaceSnapshot, visualHead: number) {
  if (!base.recording) return base.projected;
  const frontier = upperBound(base.chartEvents, visualHead);
  const cached = base.projectionCache.get(frontier);
  if (cached) return cached;
  const prefixEvents = base.chartEvents.slice(0, frontier);
  const prefix = prefixRecording(base.recording, prefixEvents, base.sourceEndMs);
  // The caller may hold a terminal snapshot while seeking. Rebuild the
  // chart-only snapshot from the same prefix so terminal facts cannot leak
  // into an earlier cursor.
  const chartSnapshot = boundedSnapshot(base.recording, prefixEvents, base.sourceEndMs, visualHead);
  const projected = raceVisuals(prefix, chartSnapshot, { evidenceEndMs: base.sourceEndMs });
  if (base.projectionCache.size >= MAX_PROJECTION_CACHE) {
    // Frontier 0 is the pinned empty projection. Evict an actual historical
    // frontier; skipping the first key (0) without finding another one leaves
    // every subsequent prefix resident for the rest of a long session.
    const oldest = [...base.projectionCache.keys()].find(key => key !== 0);
    if (oldest !== undefined) base.projectionCache.delete(oldest);
  }
  base.projectionCache.set(frontier, projected);
  return projected;
}

/** Reveal one cursor from the cached fact frontier without reducing future facts. */
export function revealSessionChart(base: SessionChartBase, snapshot: RaceSnapshot, presentation: SessionChartProjectionInput['presentation'], visualHead: number): SessionChartProjection {
  const { recording, sourceEndMs, visualCoverage } = base;
  const projected = projectionAt(base, snapshot, visualHead);
  const modelEndMs = presentation === 'live'
    ? Math.max(1, Math.min(sourceEndMs, visualHead))
    : Math.max(projected.endMs, recording?.durationMs ?? 0, snapshot.atMs);
  const revealed = revealRaceVisuals(projected, visualHead, visualCoverage);
  const visibleLastLap = Math.max(1,
    ...revealed.drivers.flatMap(driver => [
      ...driver.ranks.filter(point => point.atMs <= visualHead).map(point => point.lap),
      ...driver.pace.filter(point => point.atMs <= visualHead).map(point => point.lap),
    ]),
    presentation === 'live' && snapshot.atMs <= visualHead ? snapshot.lap : 0,
  );
  let model: RaceVisuals = presentation === 'live' ? { ...revealed, lastLap: visibleLastLap } : revealed;
  if (modelEndMs !== projected.endMs) {
    model = { ...model, endMs: modelEndMs,
      flags: model.flags.map((flag, index, all) => index === all.length - 1 && flag.toMs < modelEndMs ? { ...flag, toMs: modelEndMs } : flag) };
  }
  return { recording, visualHead, visualCoverage, sourceEndMs, modelEndMs, model };
}

/**
 * Project the immutable recording facts once, then reveal only the prefix
 * allowed by the display cursor. A terminal result snapshot is deliberately
 * not used as the chart's base, so it cannot backfill earlier timeline nodes.
 */
export function projectSessionChart({ recording, snapshot, presentation, visualHead }: SessionChartProjectionInput): SessionChartProjection {
  return revealSessionChart(projectSessionChartBase(recording, snapshot), snapshot, presentation, visualHead);
}
